import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { withSessionRecovery } from 'state/services/sessionRecovery'
import {
  userServiceBaseUrl,
  type PublicProfileCard,
  type UserProfile
} from 'state/services/indexNode/indexNode'

/**
 * The community API — people, clans and the bell.
 *
 * Its own slice rather than more endpoints on `indexNodeApi`, because it talks
 * to a different service and shares no cache tags with licences. The base url
 * is imported from there so both stay pointed at the same place: two constants
 * for one service is one rename away from half the app calling a 404.
 */

export type CommunityCard = {
  wallet: string
  name: string | null
  tag: string | null
  avatarPath: string | null
  clan: { tag: string; name: string } | null
}

export type CommunityUser = CommunityCard & {
  bio: string | null
  linkX: string | null
  linkLinkedIn: string | null
  linkFacebook: string | null
  /** Null where the chain did not answer. Never render this as a zero. */
  votingPower: number | null
}

export type HolderRow = CommunityCard & {
  rank: number
  mpro: string
  mproUnits: number
  /** Has code at its address. Not a person, whoever controls it. */
  isContract?: boolean
  /** Our own name for it, where the address is one of the platform's. */
  contractName?: string | null
}

export type ProviderRow = CommunityCard & {
  rank: number
  licenceCount: number
  votingPower: number | null
}

export type ClanCard = {
  id: string
  name: string
  tag: string
  description: string | null
  avatarPath: string | null
  leaderWallet: string
  openToRequests: boolean
  memberCount: number
}

export type ClanMemberRow = {
  wallet: string
  role: 'leader' | 'member'
  profile: { name?: string; tag?: string; avatarPath?: string } | null
}

export type ClanRequestRow = {
  _id: string
  clanId: string
  wallet: string
  kind: 'invite' | 'request'
  status: string
  byWallet: string
  message?: string
  createdAt: string
}

export type NotificationRow = {
  _id: string
  kind: string
  data: Record<string, unknown>
  readAt: string | null
  subjectId: string | null
  createdAt: string
}

/** Headers for the endpoints that need a wallet session. */
export type SessionArgs = { account: string; accessToken: string }

/**
 * The headers the platform actually uses.
 *
 * `Authorization` carries the token **bare**, with no `Bearer` scheme: that is
 * what the licences service stores when a session is created and what it
 * matches on. Sending `Bearer <token>` made the lookup compare a prefixed
 * string against an unprefixed one, and every community call was refused with
 * "Invalid session" while the person was signed in and looking at their own
 * address in the header.
 *
 * The wallet goes lowercased for the same reason: it is the form the session
 * row was written with.
 */
function sessionHeaders({ account, accessToken }: SessionArgs) {
  return {
    'x-account-wallet': account.toLowerCase(),
    authorization: accessToken
  }
}

export enum CommunityTag {
  CLAN = 'CLAN',
  MEMBERSHIP = 'MEMBERSHIP',
  PENDING = 'PENDING',
  NOTIFICATIONS = 'NOTIFICATIONS',
  CLAN_VOTE = 'CLAN_VOTE',
  PROFILE = 'PROFILE'
}

export const communityApi = createApi({
  reducerPath: 'communityApi',
  baseQuery: withSessionRecovery(fetchBaseQuery({ baseUrl: userServiceBaseUrl })),
  tagTypes: [
    CommunityTag.CLAN,
    CommunityTag.MEMBERSHIP,
    CommunityTag.PENDING,
    CommunityTag.NOTIFICATIONS,
    CommunityTag.CLAN_VOTE,
    CommunityTag.PROFILE
  ],
  endpoints: (builder) => ({
    // ------------------------------------------------------------- read-only
    getCommunityUsers: builder.query<{ users: CommunityUser[] }, { limit?: number }>({
      query: ({ limit = 50 }) => `community/users?limit=${limit}`
    }),
    /**
     * `available` is the difference between "nobody is here" and "we could not
     * find out". The tables render the second as a message, never as an empty
     * list — a leaderboard that times out is not an empty community.
     */
    getTopHolders: builder.query<
      { holders: HolderRow[]; available: boolean; computedAt: string | null; candidateCount: number; scope: string },
      { limit?: number }
    >({
      query: ({ limit = 25 }) => `community/top-holders?limit=${limit}`
    }),
    getTopLicenceProviders: builder.query<
      { providers: ProviderRow[]; available: boolean; computedAt: string | null },
      { limit?: number }
    >({
      query: ({ limit = 25 }) => `community/top-licence-providers?limit=${limit}`
    }),
    refreshLeaderboards: builder.mutation<
      { recomputed: boolean; computedAt: string | null },
      void
    >({
      query: () => ({ url: 'community/refresh', method: 'POST' })
    }),
    /** The clan's own vote on a DAO proposal: mine, the clan's, and who is awaited. */
    getMyClanVote: builder.query<
      {
        clan: {
          choice: 'yes' | 'no' | 'abstain' | null
          votedWallets: string[]
          memberWallets: string[]
          memberCount: number
          votesCast: number
          counts: { yes: number; no: number; abstain: number }
          turnout: number
          quorumMet: boolean
          status: 'waiting' | 'tied' | 'decided'
          boostEligible: boolean
        } | null
        myChoice?: 'yes' | 'no' | 'abstain' | null
        awaiting?: string[]
      },
      SessionArgs & { proposalId: string }
    >({
      query: ({ account, accessToken, proposalId }) => ({
        url: `clans/me/vote/${proposalId}`,
        headers: sessionHeaders({ account, accessToken })
      }),
      providesTags: [CommunityTag.CLAN_VOTE]
    }),
    castClanVote: builder.mutation<
      unknown,
      SessionArgs & { proposalId: string; choice: 'yes' | 'no' | 'abstain' }
    >({
      query: ({ account, accessToken, proposalId, choice }) => ({
        url: `clans/me/vote/${proposalId}`,
        method: 'POST',
        headers: sessionHeaders({ account, accessToken }),
        body: { choice }
      }),
      invalidatesTags: [CommunityTag.CLAN_VOTE]
    }),
    /** One wallet's power — public, because it is derived from public balances. */
    getVotingPower: builder.query<
      { wallet: string; votingPower: number | null },
      { wallet: string }
    >({
      query: ({ wallet }) => `community/voting-power/${wallet}`
    }),
    /** The front page. Public and unauthenticated on purpose. */
    getNews: builder.query<
      { news: { id: string; title: string; body: string; byline: string | null; publishedAt: string | null }[] },
      { limit?: number }
    >({
      query: ({ limit = 10 }) => `news?limit=${limit}`
    }),
    getNewsItem: builder.query<
      { id: string; title: string; body: string; byline: string | null; publishedAt: string | null } | null,
      { id: string }
    >({
      query: ({ id }) => `news/${id}`
    }),
getOwnProfile: builder.query<UserProfile, SessionArgs>({
      query: ({ account, accessToken }) => ({
        url: `profile/me`,
        method: 'GET',
        headers: sessionHeaders({ account, accessToken })
      }),
      providesTags: [CommunityTag.PROFILE]
    }),

    updateOwnProfile: builder.mutation<UserProfile, SessionArgs & { patch: Partial<UserProfile> }>({
      query: ({ account, accessToken, patch }) => ({
        url: `profile/me`,
        method: 'PUT',
        headers: sessionHeaders({ account, accessToken }),
        body: patch
      }),
      invalidatesTags: [CommunityTag.PROFILE]
    }),

    uploadOwnAvatar: builder.mutation<{ avatarPath: string }, SessionArgs & { file: Blob }>({
      query: ({ account, accessToken, file }) => ({
        url: `profile/me/avatar`,
        method: 'POST',
        headers: sessionHeaders({ account, accessToken }),
        body: file
      }),
      invalidatesTags: [CommunityTag.PROFILE]
    }),

    removeOwnAvatar: builder.mutation<{ avatarPath: null }, SessionArgs>({
      query: ({ account, accessToken }) => ({
        url: `profile/me/avatar`,
        method: 'DELETE',
        headers: sessionHeaders({ account, accessToken })
      }),
      invalidatesTags: [CommunityTag.PROFILE]
    }),

    getPublicProfile: builder.query<PublicProfileCard | null, { tag: string }>({
      query: ({ tag }) => ({
        url: `profile/public/${tag}`,
        method: 'GET'
      })
    }),

    resolvePublicProfiles: builder.query<
      { profiles: Record<string, { name?: string; tag?: string; avatarPath?: string }> },
      { wallets: string[] }
    >({
      query: ({ wallets }) => ({
        url: `profile/public/resolve`,
        method: 'POST',
        body: { wallets }
      })
    }),
    getClans: builder.query<{ clans: ClanCard[] }, { search?: string }>({
      query: ({ search }) =>
        `clans?limit=50${search ? `&search=${encodeURIComponent(search)}` : ''}`,
      providesTags: [CommunityTag.CLAN]
    }),
    getClan: builder.query<{ clan: ClanCard; members: ClanMemberRow[] } | null, { tag: string }>({
      query: ({ tag }) => `clans/${tag}`,
      providesTags: [CommunityTag.CLAN]
    }),
    getClanPower: builder.query<
      { pooledVotingPower: number; memberCount: number },
      { clanId: string }
    >({
      query: ({ clanId }) => `community/clans/${clanId}/power`
    }),

    // ------------------------------------------------------------- signed in
    getMyMembership: builder.query<
      { clan: ClanCard | null; role: 'leader' | 'member' | null },
      SessionArgs
    >({
      query: (args) => ({ url: 'clans/me/membership', headers: sessionHeaders(args) }),
      providesTags: [CommunityTag.MEMBERSHIP]
    }),
    getMyPending: builder.query<
      {
        invites: ClanRequestRow[]
        requests: ClanRequestRow[]
        /**
         * The requests I have sent and nobody has answered yet.
         *
         * Without them the clan list cannot know it has already asked, so it
         * keeps offering "Ask to join" and the second press comes back a 409 —
         * a rule that was enforced and invisible, which is a rule experienced
         * as a bug.
         */
        outgoing?: (ClanRequestRow & { clanName?: string | null; clanTag?: string | null })[]
      },
      SessionArgs
    >({
      query: (args) => ({ url: 'clans/me/pending', headers: sessionHeaders(args) }),
      providesTags: [CommunityTag.PENDING]
    }),
    /**
     * The picture, posted as raw bytes.
     *
     * `prepareHeaders` is not used and the body is a Blob, so the browser sets
     * no JSON content type and RTK Query does not stringify it. The storage
     * secret lives two services away; the page only proves who it is.
     */
    uploadClanAvatar: builder.mutation<{ avatarPath: string }, SessionArgs & { file: Blob }>({
      query: ({ account, accessToken, file }) => ({
        url: 'clans/me/avatar',
        method: 'POST',
        headers: sessionHeaders({ account, accessToken }),
        body: file
      }),
      invalidatesTags: [CommunityTag.CLAN, CommunityTag.MEMBERSHIP]
    }),
    createClan: builder.mutation<
      ClanCard,
      SessionArgs & { name: string; tag: string; description?: string; openToRequests?: boolean }
    >({
      query: ({ account, accessToken, ...body }) => ({
        url: 'clans',
        method: 'POST',
        headers: sessionHeaders({ account, accessToken }),
        body
      }),
      invalidatesTags: [CommunityTag.MEMBERSHIP, CommunityTag.CLAN]
    }),
    updateMyClan: builder.mutation<
      ClanCard,
      SessionArgs & { name?: string; description?: string; openToRequests?: boolean }
    >({
      query: ({ account, accessToken, ...body }) => ({
        url: 'clans/me',
        method: 'PUT',
        headers: sessionHeaders({ account, accessToken }),
        body
      }),
      invalidatesTags: [CommunityTag.MEMBERSHIP, CommunityTag.CLAN]
    }),
    inviteToClan: builder.mutation<unknown, SessionArgs & { wallet: string; message?: string }>({
      query: ({ account, accessToken, ...body }) => ({
        url: 'clans/me/invite',
        method: 'POST',
        headers: sessionHeaders({ account, accessToken }),
        body
      }),
      invalidatesTags: [CommunityTag.PENDING]
    }),
    requestToJoinClan: builder.mutation<unknown, SessionArgs & { tag: string; message?: string }>({
      query: ({ account, accessToken, tag, message }) => ({
        url: `clans/${tag}/request`,
        method: 'POST',
        headers: sessionHeaders({ account, accessToken }),
        body: { message }
      }),
      invalidatesTags: [CommunityTag.PENDING]
    }),
    respondToClanRequest: builder.mutation<
      unknown,
      SessionArgs & { requestId: string; accept: boolean }
    >({
      query: ({ account, accessToken, requestId, accept }) => ({
        url: `clans/requests/${requestId}/respond`,
        method: 'POST',
        headers: sessionHeaders({ account, accessToken }),
        body: { accept }
      }),
      invalidatesTags: [
        CommunityTag.PENDING,
        CommunityTag.MEMBERSHIP,
        CommunityTag.CLAN,
        CommunityTag.NOTIFICATIONS
      ]
    }),
    cancelClanRequest: builder.mutation<unknown, SessionArgs & { requestId: string }>({
      query: ({ account, accessToken, requestId }) => ({
        url: `clans/requests/${requestId}/cancel`,
        method: 'POST',
        headers: sessionHeaders({ account, accessToken })
      }),
      invalidatesTags: [CommunityTag.PENDING]
    }),
    leaveClan: builder.mutation<unknown, SessionArgs>({
      query: (args) => ({
        url: 'clans/me/leave',
        method: 'POST',
        headers: sessionHeaders(args)
      }),
      invalidatesTags: [CommunityTag.MEMBERSHIP, CommunityTag.CLAN]
    }),
    removeClanMember: builder.mutation<unknown, SessionArgs & { wallet: string }>({
      query: ({ account, accessToken, wallet }) => ({
        url: 'clans/me/members/remove',
        method: 'POST',
        headers: sessionHeaders({ account, accessToken }),
        body: { wallet }
      }),
      invalidatesTags: [CommunityTag.CLAN, CommunityTag.MEMBERSHIP]
    }),
    transferClanLeadership: builder.mutation<unknown, SessionArgs & { wallet: string }>({
      query: ({ account, accessToken, wallet }) => ({
        url: 'clans/me/transfer-leadership',
        method: 'POST',
        headers: sessionHeaders({ account, accessToken }),
        body: { wallet }
      }),
      invalidatesTags: [CommunityTag.CLAN, CommunityTag.MEMBERSHIP]
    }),

    // ------------------------------------------------------------------ bell
    getNotifications: builder.query<
      { notifications: NotificationRow[]; total: number },
      SessionArgs & { skip?: number; limit?: number; unreadOnly?: boolean }
    >({
      query: ({ account, accessToken, skip = 0, limit = 30, unreadOnly = false }) => ({
        url: `notifications?skip=${skip}&limit=${limit}${unreadOnly ? '&unread=true' : ''}`,
        headers: sessionHeaders({ account, accessToken })
      }),
      providesTags: [CommunityTag.NOTIFICATIONS]
    }),
    deleteNotification: builder.mutation<unknown, SessionArgs & { id: string }>({
      query: ({ account, accessToken, id }) => ({
        url: `notifications/${id}`,
        method: 'DELETE',
        headers: sessionHeaders({ account, accessToken })
      }),
      invalidatesTags: [CommunityTag.NOTIFICATIONS]
    }),
    deleteReadNotifications: builder.mutation<{ deleted: number }, SessionArgs>({
      query: (args) => ({
        url: 'notifications/read',
        method: 'DELETE',
        headers: sessionHeaders(args)
      }),
      invalidatesTags: [CommunityTag.NOTIFICATIONS]
    }),
    getUnreadCount: builder.query<{ unread: number }, SessionArgs>({
      query: (args) => ({ url: 'notifications/unread-count', headers: sessionHeaders(args) }),
      providesTags: [CommunityTag.NOTIFICATIONS]
    }),
    markNotificationsRead: builder.mutation<unknown, SessionArgs & { ids: string[] }>({
      query: ({ account, accessToken, ids }) => ({
        url: 'notifications/read',
        method: 'POST',
        headers: sessionHeaders({ account, accessToken }),
        body: { ids }
      }),
      invalidatesTags: [CommunityTag.NOTIFICATIONS]
    }),
    markAllNotificationsRead: builder.mutation<unknown, SessionArgs>({
      query: (args) => ({
        url: 'notifications/read-all',
        method: 'POST',
        headers: sessionHeaders(args)
      }),
      invalidatesTags: [CommunityTag.NOTIFICATIONS]
    })
  })
})

export const {
  useGetCommunityUsersQuery,
  useGetTopHoldersQuery,
  useGetTopLicenceProvidersQuery,
  useRefreshLeaderboardsMutation,
  useGetVotingPowerQuery,
  useGetOwnProfileQuery,
  useUpdateOwnProfileMutation,
  useUploadOwnAvatarMutation,
  useRemoveOwnAvatarMutation,
  useGetPublicProfileQuery,
  useResolvePublicProfilesQuery,
  useGetNewsQuery,
  useGetNewsItemQuery,
  useGetMyClanVoteQuery,
  useCastClanVoteMutation,
  useGetClansQuery,
  useGetClanQuery,
  useGetClanPowerQuery,
  useGetMyMembershipQuery,
  useGetMyPendingQuery,
  useUploadClanAvatarMutation,
  useCreateClanMutation,
  useUpdateMyClanMutation,
  useInviteToClanMutation,
  useRequestToJoinClanMutation,
  useRespondToClanRequestMutation,
  useCancelClanRequestMutation,
  useLeaveClanMutation,
  useRemoveClanMemberMutation,
  useTransferClanLeadershipMutation,
  useGetNotificationsQuery,
  useDeleteNotificationMutation,
  useDeleteReadNotificationsMutation,
  useGetUnreadCountQuery,
  useMarkNotificationsReadMutation,
  useMarkAllNotificationsReadMutation
} = communityApi
